/**
 * ForgotPassword - 3-step self-serve password reset
 *
 * Step 1: user enters email or phone + picks channel (SMS or Email) → OTP sent
 * Step 2: user enters 6-digit code → gets short-lived reset_token
 * Step 3: user sets new password → password updated, redirect to /login
 *
 * Backend: /api/auth/forgot-password/{start,verify-otp,reset}
 */
import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Alert, AlertDescription } from '../components/ui/alert';
import { AlertCircle, Loader2, KeyRound, ArrowLeft, CheckCircle2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ForgotPassword() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [identifier, setIdentifier] = useState('');
  const [channel, setChannel] = useState('email');
  const [session, setSession] = useState(null);
  const [code, setCode] = useState('');
  const [resetToken, setResetToken] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const err = (e) => {
    const d = e?.response?.data?.detail;
    return typeof d === 'string' ? d : d?.message || 'Something went wrong. Please try again.';
  };

  const handleStart = async () => {
    setError(''); setLoading(true);
    try {
      const isEmail = identifier.includes('@');
      const { data } = await axios.post(`${API_URL}/api/auth/forgot-password/start`, {
        identifier: identifier.trim(),
        channel: isEmail && channel === 'sms' ? 'email' : channel,
      });
      setSession(data);
      setStep(2);
    } catch (e) { setError(err(e)); }
    setLoading(false);
  };

  const handleVerify = async () => {
    setError(''); setLoading(true);
    try {
      const { data } = await axios.post(`${API_URL}/api/auth/forgot-password/verify-otp`, {
        reset_session_id: session.reset_session_id, code,
      });
      setResetToken(data.reset_token);
      setStep(3);
    } catch (e) { setError(err(e)); }
    setLoading(false);
  };

  const handleReset = async () => {
    setError('');
    if (newPassword !== confirmPassword) { setError("Passwords don't match"); return; }
    if (newPassword.length < 8) { setError('Password must be at least 8 characters'); return; }
    setLoading(true);
    try {
      await axios.post(`${API_URL}/api/auth/forgot-password/reset`, {
        reset_token: resetToken, new_password: newPassword,
      });
      setStep(4);
      setTimeout(() => navigate('/login'), 3000);
    } catch (e) { setError(err(e)); }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white flex items-start justify-center py-12 px-4">
      <div className="w-full max-w-md">
        <Card data-testid="forgot-password-card">
          <CardHeader>
            <CardTitle className="text-2xl flex items-center gap-2">
              <KeyRound className="w-6 h-6 text-blue-600" />
              {step === 1 && 'Forgot your password?'}
              {step === 2 && 'Enter the code'}
              {step === 3 && 'Choose a new password'}
              {step === 4 && 'Password updated'}
            </CardTitle>
            <CardDescription>
              {step === 1 && 'Enter your email or phone number and we\'ll send a code to reset your password.'}
              {step === 2 && session && `Code sent to ${session.masked_target} by ${session.channel === 'sms' ? 'SMS' : 'email'}.`}
              {step === 3 && 'Set a strong password you haven\'t used before.'}
              {step === 4 && 'You can now sign in with your new password.'}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {step === 1 && (
              <>
                <div>
                  <Label htmlFor="identifier">Email or phone number</Label>
                  <Input
                    id="identifier"
                    placeholder="you@example.com or +44…"
                    value={identifier}
                    onChange={(e) => setIdentifier(e.target.value)}
                    data-testid="forgot-identifier"
                  />
                </div>
                <div>
                  <Label>Send code by</Label>
                  <div className="flex gap-2 mt-2">
                    <Button
                      type="button"
                      variant={channel === 'email' ? 'default' : 'outline'}
                      onClick={() => setChannel('email')}
                      className="flex-1"
                      data-testid="forgot-channel-email"
                    >
                      Email
                    </Button>
                    <Button
                      type="button"
                      variant={channel === 'sms' ? 'default' : 'outline'}
                      onClick={() => setChannel('sms')}
                      className="flex-1"
                      data-testid="forgot-channel-sms"
                    >
                      SMS
                    </Button>
                  </div>
                </div>
                {error && (
                  <Alert variant="destructive"><AlertCircle className="w-4 h-4" /><AlertDescription>{error}</AlertDescription></Alert>
                )}
                <Button
                  onClick={handleStart}
                  disabled={loading || !identifier}
                  className="w-full"
                  data-testid="forgot-send-code-btn"
                >
                  {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                  Send reset code
                </Button>
                <p className="text-center text-sm">
                  <Link to="/login" className="text-blue-600 hover:underline">← Back to sign in</Link>
                </p>
              </>
            )}

            {step === 2 && (
              <>
                <div>
                  <Label htmlFor="code">6-digit code</Label>
                  <Input
                    id="code"
                    type="text"
                    inputMode="numeric"
                    maxLength={6}
                    value={code}
                    onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    className="text-center text-2xl tracking-widest"
                    data-testid="forgot-otp-input"
                  />
                </div>
                {error && (
                  <Alert variant="destructive"><AlertCircle className="w-4 h-4" /><AlertDescription>{error}</AlertDescription></Alert>
                )}
                <Button onClick={handleVerify} disabled={loading || code.length < 4} className="w-full" data-testid="forgot-verify-btn">
                  {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                  Verify code
                </Button>
                <Button variant="ghost" size="sm" onClick={() => setStep(1)} data-testid="forgot-back-btn">
                  <ArrowLeft className="w-4 h-4 mr-1" /> Change email or phone
                </Button>
              </>
            )}

            {step === 3 && (
              <>
                <div>
                  <Label htmlFor="new-password">New password</Label>
                  <Input
                    id="new-password"
                    type="password"
                    placeholder="At least 8 characters"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    data-testid="forgot-new-password"
                  />
                </div>
                <div>
                  <Label htmlFor="confirm-password">Confirm new password</Label>
                  <Input
                    id="confirm-password"
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    data-testid="forgot-confirm-password"
                  />
                </div>
                {error && (
                  <Alert variant="destructive"><AlertCircle className="w-4 h-4" /><AlertDescription>{error}</AlertDescription></Alert>
                )}
                <Button
                  onClick={handleReset}
                  disabled={loading || !newPassword || newPassword !== confirmPassword}
                  className="w-full"
                  data-testid="forgot-reset-btn"
                >
                  {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                  Update password
                </Button>
              </>
            )}

            {step === 4 && (
              <div className="text-center py-6 space-y-3" data-testid="forgot-success">
                <CheckCircle2 className="w-14 h-14 text-green-500 mx-auto" />
                <p className="text-lg font-semibold">Password updated successfully!</p>
                <p className="text-sm text-gray-600">Redirecting you to sign in…</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
